/**
 * How a storage location's quota reads (1.0.0-rc.5): what PRISM keeps there against the quota ("136 MB used of
 * 30.0 GB (0.5 %)"), and a separate note when the disk has less free space than the quota still allows. Before rc.5
 * the cards showed the usable space (the smaller of what the quota leaves and the disk's free space) as "free of"
 * the quota, so a disk filled by other data looked like PRISM had used most of its quota.
 */
import { bytes } from './format';

export interface QuotaLike { limitBytes: number; usedBytes: number }

/** The share of the quota used, in percent (0–100), for bars. */
export const quotaPercent = (q: QuotaLike): number => (q.limitBytes > 0 ? Math.min(100, Math.max(0, (q.usedBytes / q.limitBytes) * 100)) : 0);

/** "0.5 %", "12 %", "100 %": one decimal below 10 %, whole numbers above; never "0 %" for a little use. */
export function percentLabel(p: number): string {
  if (p > 0 && p < 0.1) return '< 0.1 %';
  if (p === 0) return '0 %';
  return `${p < 10 ? (Math.round(p * 10) / 10).toFixed(1) : Math.floor(p)} %`;
}

/** "136 MB used of 30.0 GB (0.5 %)". */
export const quotaUsedLabel = (q: QuotaLike): string => `${bytes(q.usedBytes)} used of ${bytes(q.limitBytes)} (${percentLabel(quotaPercent(q))})`;

/** "Disk has only 24.8 GB free, less than the quota allows" when the disk, not the quota, is what limits; else null. */
export function diskBelowQuotaNote(q: QuotaLike, diskFreeBytes: number | null | undefined): string | null {
  if (diskFreeBytes == null) return null;
  const quotaLeft = Math.max(0, q.limitBytes - q.usedBytes);
  return diskFreeBytes < quotaLeft ? `Disk has only ${bytes(diskFreeBytes)} free, less than the quota allows` : null;
}
